import {
    TextField,
    Button,
    Box,
    Typography,
    styled
} from '@mui/material';

export const FullHeightBox = styled(Box)(({ theme }) => ({
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
            radial-gradient(circle at 20% 30%, rgba(220, 53, 69, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(220, 53, 69, 0.05) 0%, transparent 50%),
            repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                rgba(255, 255, 255, 0.03) 10px,
                rgba(255, 255, 255, 0.03) 20px
            )
        `,
        pointerEvents: 'none',
    },
}));

export const TitleTypography = styled(Typography)(({ theme }) => ({
    color: '#dc3545',
    marginBottom: '0.5rem',
    fontWeight: 700,
    fontSize: theme.breakpoints.down('xs') ? '1.1rem' : '1.3rem',
    letterSpacing: '4px',
    textTransform: 'uppercase',
    position: 'relative',
    zIndex: 1,
}));

export const SubtitleTypography = styled(Typography)(({ theme }) => ({
    color: '#2c3e50',
    marginBottom: '3rem',
    fontWeight: 600,
    fontSize: theme.breakpoints.down('xs') ? '1.6rem' : '2.2rem',
    letterSpacing: '0.5px',
    position: 'relative',
    zIndex: 1,
    '& b': {
        color: '#dc3545',
        fontWeight: 700,
    },
}));

export const AbsoluteBox = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '1.5rem',
    right: '1.5rem',
    padding: '0.5rem',
    display: 'flex',
    gap: '0.75rem',
    zIndex: 10,
    [theme.breakpoints.down('xs')]: {
        top: '1rem',
        right: '1rem',
        gap: '0.5rem',
    },
}));

export const FlagButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== 'backgroundImage',
})(({ theme, backgroundImage }) => ({
    width: '44px',
    height: '30px',
    padding: 0,
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    minWidth: 'unset',
    borderRadius: '6px',
    border: '2px solid rgba(255, 255, 255, 0.8)',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.2s ease',
    '&:hover': {
        transform: 'scale(1.05)',
        borderColor: '#ffffff',
        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
    },
}));

export const FormContainer = styled(Box)(({ theme }) => ({
    backgroundColor: '#ffffff',
    padding: theme.breakpoints.down('xs') ? '2.5rem 1.5rem' : '3.5rem 2.5rem',
    borderRadius: '16px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
    textAlign: 'center',
    width: '100%',
    maxWidth: '420px',
    position: 'relative',
    zIndex: 1,
    animation: 'slideUp 0.5s ease-out',
    '@keyframes slideUp': {
        from: {
            opacity: 0,
            transform: 'translateY(20px)',
        },
        to: {
            opacity: 1,
            transform: 'translateY(0)',
        },
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: 'linear-gradient(90deg, #dc3545 0%, #c82333 100%)',
        borderRadius: '16px 16px 0 0',
    },
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        transition: 'all 0.2s ease',
        '& fieldset': {
            borderColor: '#dee2e6',
            borderWidth: '1.5px',
            transition: 'all 0.2s ease',
        },
        '&:hover fieldset': {
            borderColor: '#adb5bd',
        },
        '&.Mui-focused': {
            backgroundColor: '#ffffff',
            '& fieldset': {
                borderColor: '#dc3545',
                borderWidth: '2px',
                boxShadow: '0 0 0 3px rgba(220, 53, 69, 0.08)',
            },
        },
    },
    '& .MuiInputLabel-root': {
        color: '#6c757d',
        fontWeight: 500,
        fontSize: '0.95rem',
        '&.Mui-focused': {
            color: '#dc3545',
        },
    },
    '& .MuiInputBase-input': {
        color: '#212529',
        fontWeight: 400,
        fontSize: '0.95rem',
        padding: '12px 14px',
        '&::placeholder': {
            color: '#adb5bd',
            opacity: 1,
        },
    },
}));

export const SubmitButton = styled(Button)(({ theme }) => ({
    padding: '0.875rem 2rem',
    borderRadius: '8px',
    background: '#dc3545',
    color: 'white',
    fontWeight: 600,
    fontSize: '1rem',
    textTransform: 'none',
    letterSpacing: '0.3px',
    boxShadow: '0 2px 8px rgba(220, 53, 69, 0.25)',
    transition: 'all 0.2s ease',
    '&:hover': {
        background: '#c82333',
        boxShadow: '0 4px 12px rgba(220, 53, 69, 0.35)',
        transform: 'translateY(-1px)',
    },
    '&:active': {
        transform: 'translateY(0)',
        boxShadow: '0 2px 6px rgba(220, 53, 69, 0.25)',
    },
}));